import React, { useMemo } from "react";

import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import {
  encodingColumns,
  getEncodingHref,
  getEncodingSortField,
  toEncodingStatus,
  toEncodingType,
} from "./tableParams/encodingColumns";
import EncoderAPI from "../../api/EncoderAPI";
import { selectPaginatedPage } from "../../hooks/selectPaginatedPage";
import useDataTableQuery from "../../hooks/useDataTableQuery";
import useDataTableServerState from "../../hooks/useDataTableServerState";
import CappedPage from "../common/CappedPage";
import DataTable from "../dataTable/DataTable";

import type { JSX } from "react";

import type { EncodingJob } from "../../api/EncoderAPI.types";
import type { AppState } from "../../reducers";

const REFETCH_INTERVAL = 30000;

function EncodingList(): JSX.Element {
  const { t } = useTranslation("editor-encoding");

  const hasFastOption = useSelector(
    (state: AppState) =>
      state.fetchReducer.orga_config.data?.organization_options?.allow_fast ??
      false,
  );

  const columnList = useMemo(
    () => encodingColumns({ t, hasFastOption }),
    [t, hasFastOption],
  );

  const serverState = useDataTableServerState<EncodingJob>({
    columns: columnList,
    defaultSorting: { id: "date", desc: true },
  });

  const status = toEncodingStatus(serverState.filters.status);
  const type = toEncodingType(serverState.filters.type);

  const getEncodingListQuery = EncoderAPI.generateQuery({
    fn: EncoderAPI.getEncodingList,
    params: [
      {
        page: serverState.page,
        perPage: serverState.perPage,
        filters: {
          ...(status ? { status } : {}),
          ...(type ? { type } : {}),
        },
        sort: serverState.order && {
          field: getEncodingSortField(serverState.order.field),
          direction: serverState.order.direction,
        },
      },
    ],
    options: { refetchInterval: REFETCH_INTERVAL },
  });

  const encodingTable = useDataTableQuery(
    getEncodingListQuery,
    selectPaginatedPage,
  );

  return (
    <CappedPage contentClassName="py-15">
      <DataTable
        columns={columnList}
        data={encodingTable.data}
        getRowHref={getEncodingHref}
        isError={encodingTable.isError}
        isLoading={encodingTable.isLoading}
        onRetry={encodingTable.onRetry}
        rowCount={encodingTable.rowCount}
        serverState={serverState}
        title={t("encodingList")}
      />
    </CappedPage>
  );
}

export default EncodingList;
